import { ISpreadsheet, SpreadsheetEvents, IBufferManager, ICopyData } from "../types";
import {
	getRangeMatrix,
	isRangeId,
	getCellInfo,
	getCellIndex,
	getCellNameByIndex,
	getCellIds,
	getRangeIndexes,
	getNextRangeCell,
	getPrevRangeCell,
	parseHtmlTable,
	parseFromText,
	isMac,
	getCellNameById,
	isCellSpaned,
} from "./main";
import { IGrid, ICol } from "@dhx/ts-grid";
import { EditLine } from "../EditLine";
import { getToggledValue } from "./toolbar";
import { focusManager } from "../../../ts-common/FocusManager";
import { DataPage } from "@xbs/muon";
import { IDataCollection } from "@dhx/ts-data";
import { isDefined } from "@dhx/ts-common/core";

export function isPrintableKey(e: KeyboardEvent): boolean {
	if (e.ctrlKey || e.altKey || e.metaKey || (e.shiftKey && e.keyCode === 32)) {
		return;
	}
	const keyCode = e.which || e.keyCode;
	const symbols = {
		32: " ",

		106: "*",
		107: "+",
		109: "-",
		110: ".",
		111: "/",

		186: ";",
		187: "=",
		188: ",",
		189: "-",
		190: ".",
		191: "/",
		192: "`",
		219: "[",
		220: "\\",
		221: "]",
		222: "'",
	};

	const isLetter = keyCode >= 65 && keyCode <= 90;
	const isNumber = keyCode >= 48 && keyCode <= 57;
	const isNumpadNumber = keyCode >= 96 && keyCode <= 105;
	const isSymbol = !!symbols[keyCode];

	return isLetter || isNumber || isNumpadNumber || isSymbol || false;
}

export function getHtmlData(spreadsheet: ISpreadsheet) {
	let selected = spreadsheet.selection.getSelectedCell();
	if (selected.includes(",")) {
		const splited = selected.split(",");
		selected = splited[splited.length - 1];
	}
	let matrix;
	if (!isRangeId(selected)) {
		matrix = [[selected]];
	} else {
		matrix = getRangeMatrix(selected, (spreadsheet as any)._grid) as any;
	}

	const values = matrix.reduce((total: string, row: string[]) => {
		const rowValues = row.reduce((t, cell) => {
			const val = spreadsheet.getValue(cell) || " ";
			const stylesObj = spreadsheet.getStyle(cell) || {};
			const styles = Object.keys(stylesObj).reduce(
				(totalStyles, key) => `${totalStyles}${key}:${stylesObj[key]};`,
				""
			);
			return `${t} <th style="${styles}">
			${val}</th>`;
		}, "");
		return `${total}<tr style="box-sizing: inherit;">${rowValues}</tr>`;
	}, "");

	return `
	<!--StartFragment-->
		<table id="customers">
			<tbody style="box-sizing: inherit;">
				${values}
			</tbody>
		</table>
	<!--EndFragment-->`;
}

function getLength(grid: IGrid, dir: IDir): { min: number; max: number } {
	if (dir === "row") {
		return {
			min: 0,
			max: grid.data.getLength() - 1,
		};
	}
	return {
		min: 1,
		max: grid.config.columns.length - 1,
	};
}

type IDir = "row" | "col";

function setCell(
	spreadsheet: ISpreadsheet,
	grid: IGrid,
	set: number,
	page: DataPage,
	add?: number,
	dir?: IDir
) {
	const prevCell = spreadsheet.selection.getFocusedCell();
	const info = getCellInfo(prevCell, page, grid);
	if (info.edited) {
		return;
	}


	const index = getCellIndex(prevCell, grid);
	const length = getLength(grid, dir);

	const nextVal = add ? index[dir] + add : set;
	if (nextVal >= length.min && nextVal <= length.max) {
		index[dir] = nextVal;
	}
	let cell = getCellNameByIndex(index.row, index.col, grid);
	const {merged} = page.getMeta();
	if (merged?.length) {
		const nextSpan = isCellSpaned(cell, merged, grid); 
		if (nextSpan) {
			const prevSpan = isCellSpaned(prevCell, merged, grid);
			const spanDir = dir === "col" ? "column" : "row";
			const span = {from: {...nextSpan.from}, to: {...nextSpan.to}}
			span.from.column += 1;
			span.to.column += 1;
			if (nextSpan === prevSpan) {
				const nextVal = add > 0 ? span.to[spanDir] + 1 : span.from[spanDir] - 1
				if (nextVal >= length.min && nextVal <= length.max) {
					index[dir] = nextVal;
				}
				cell = getCellNameByIndex(index.row, index.col, grid);
			} else {
				const row = spanDir === "row" ? span.from.row : index.row;
				const col = spanDir === "column" ? span.from.column : index.col;
				cell = getCellNameByIndex(row, col, grid);
			}
			
		}
	}
	spreadsheet.selection.setSelectedCell(cell);

	const id = getCellIds(cell, grid);
	grid.scrollTo(`${id.row + 1}`, `${id.col + 1}`);
}

function setSelection(spreadsheet: ISpreadsheet, grid: IGrid, step: number, dir: IDir, page: DataPage) {
	const focused = spreadsheet.selection.getFocusedCell();
	const info = getCellInfo(focused, page, grid);
	if (info.edited) {
		return;
	}

	const selectedIndex = getCellIndex(focused, grid);

	const lastSelected = spreadsheet.selection.getSelectedCell().split(",")[0];

	let index;
	let end;
	if (!lastSelected.includes(":")) {
		index = { ...selectedIndex };
		end = selectedIndex;
	} else {
		const range = getRangeIndexes(lastSelected, grid);

		if (range.end[dir] > selectedIndex[dir]) {
			index = { ...range.end };
			end = range.start;
		} else {
			index = { ...range.start };
			end = range.end;
		}
	}
	const length = getLength(grid, dir);
	if (index[dir] + step >= length.min && index[dir] + step <= length.max) {
		index[dir] += step;

		const start = getCellNameByIndex(index.row, index.col, grid);
		end = getCellNameByIndex(end.row, end.col, grid);
		if (start === end) {
			spreadsheet.selection.setSelectedCell(start);
			return;
		}

		spreadsheet.selection.setSelectedCell(`${start}:${end}`);
		spreadsheet.selection.setFocusedCell(focused);
	}
}

export function getKeysHandlers(
	spreadsheet: ISpreadsheet,
	grid: IGrid,
	bufferManager: IBufferManager,
	editLine: EditLine
) {
	let editedCell: string;
	let page: DataPage;

	spreadsheet.events.on(SpreadsheetEvents.afterEditStart, cell => {
		editedCell = cell;
	});

	spreadsheet.events.on(SpreadsheetEvents.afterEditEnd, () => {
		editedCell = null;
	});

	spreadsheet.events.on(
		SpreadsheetEvents.afterSheetChange,
		() => (page = (spreadsheet as any)._activePage)
	);

	document.addEventListener("keydown", (e: KeyboardEvent) => {
		if (
			isPrintableKey(e) &&
			(focusManager.getFocusId() === (spreadsheet as any)._uid ||
				focusManager.getFocusId() === (grid as any)._uid)
		) {
			const focused = spreadsheet.selection.getFocusedCell();
			if (focused && focused !== editedCell) {
				e.preventDefault();
				spreadsheet.startEdit(focused, e.key);
			}
		}
	});

	const pasteFromClipboard = (data: ICopyData, html: string) => {
		const buffer = bufferManager.getStruct();
		const focus = spreadsheet.selection.getSelectedCell();
		const to = getCellIndex(focus, grid);
		const lastCell = getCellNameByIndex(to.row + data.rowsCount - 1, to.col + data.colsCount - 1, grid);
		let cell = isRangeId(focus) ? focus : `${focus}:${lastCell}`;
		if (data.styles) {
			buffer.styles = data.styles;
		} else {
			buffer.styles = spreadsheet.getStyle(cell);
		}
		if (isDefined(html) && !html.includes(buffer.html)) {
			buffer.cells = "";
		}
		const prevVal = buffer.value;
		buffer.value = data.values;
		buffer.cell = cell;
		bufferManager.paste();
		buffer.value = prevVal;
	};

	let clipboardData = {};
	if ((window as any).clipboardData) {
		clipboardData = {
			"ctrl+c": () => {
				if (!editedCell) {
					bufferManager.store("copy");
					if ((window as any).clipboardData) {
						(window as any).clipboardData.setData("text/html", getHtmlData(spreadsheet));
						(window as any).clipboardData.setData(
							"text/plain",
							bufferManager.getStruct().value as string
						);
						(window as any).preventDefault();
						bufferManager.getStruct().html = (window as any).clipboardData.getData("text/html");
					}
				}
			},
			"ctrl+x": () => {
				if (!editedCell) {
					bufferManager.store("cut");
					if ((window as any).clipboardData) {
						(window as any).clipboardData.setData("text/html", getHtmlData(spreadsheet));
						(window as any).clipboardData.setData(
							"text/plain",
							bufferManager.getStruct().value as string
						);
						(window as any).preventDefault();
						bufferManager.getStruct().html = (window as any).clipboardData.getData("text/html");
					}
				}
			},
			"ctrl+v": () => {
				if (!editedCell) {
					if ((window as any).clipboardData) {
						let data = parseFromText((window as any).clipboardData.getData("text"));
						const html = (window as any).clipboardData.getData("text/html");
						if (html && html.includes("<table")) {
							data = parseHtmlTable(html);
						}
						pasteFromClipboard(data, html || "");
					}
				}
			},
		};
	} else {
		document.addEventListener("cut", (e: ClipboardEvent) => {
			if (
				!editedCell &&
				(focusManager.getFocusId() === (spreadsheet as any)._uid ||
					focusManager.getFocusId() === (grid as any)._uid)
			) {
				bufferManager.store("cut");
				e.clipboardData.setData("text/html", getHtmlData(spreadsheet));
				e.clipboardData.setData("text/plain", bufferManager.getStruct().value as string);
				e.preventDefault();
				bufferManager.getStruct().html = e.clipboardData.getData("text/html");
			}
		});
		document.addEventListener("copy", (e: ClipboardEvent) => {
			if (
				!editedCell &&
				(focusManager.getFocusId() === (spreadsheet as any)._uid ||
					focusManager.getFocusId() === (grid as any)._uid)
			) {
				bufferManager.store("copy");

				e.clipboardData.setData("text/html", getHtmlData(spreadsheet));
				e.clipboardData.setData("text/plain", bufferManager.getStruct().value as string);
				e.preventDefault();
				bufferManager.getStruct().html = e.clipboardData.getData("text/html");
			}
		});
		document.addEventListener("paste", (e: ClipboardEvent) => {
			if (
				!editedCell &&
				(focusManager.getFocusId() === (spreadsheet as any)._uid ||
					focusManager.getFocusId() === (grid as any)._uid)
			) {
				e.preventDefault();
				const html = e.clipboardData.getData("text/html");
				let data = parseFromText(e.clipboardData.getData("text"));
				if (html && html.includes("<table")) {
					data = parseHtmlTable(html);
				}
				pasteFromClipboard(data, html || "");
			}
		});
	}

	function lineThrough(e) {
		e.preventDefault();
		const cell = spreadsheet.selection.getSelectedCell();
		const val = getToggledValue(page, cell, "text-decoration", "line-through", grid);
		spreadsheet.setStyle(spreadsheet.selection.getSelectedCell(), {
			"text-decoration": val,
		});
	}

	function textAlign(align: "center" | "left" | "right", e: KeyboardEvent) {
		e.preventDefault();
		spreadsheet.setStyle(spreadsheet.selection.getSelectedCell(), {
			"text-align": align,
		});
	}

	function navigateSheets(dir, e) {
		e.preventDefault();
		const sheets = (spreadsheet as any)._sheets as IDataCollection;
		const active = spreadsheet.getActiveSheet().id;
		const currentIndex = sheets.getIndex(active);
		const lastIndex = sheets.getLength() - 1;
		let index = currentIndex + dir;
		const firstIndex = sheets.getId(0) === "add-sheet" ? 1 : 0;
		if (index < firstIndex) {
			index = lastIndex;
		}
		if (index > lastIndex) {
			index = firstIndex;
		}
		spreadsheet.setActiveSheet(sheets.getId(index));
	}

	function selectAction(collumn: boolean, e) {
		e.preventDefault();
		const focused = spreadsheet.selection.getFocusedCell();
		const { row, col } = getCellIds(focused, grid);
		const lastCell = getCellNameById(
			grid,
			collumn ? grid.data.getLength() : row + 1,
			collumn ? col + 1 : (grid.config.columns as ICol[]).length - 1
		);
		const firstCell = getCellNameById(
			grid,
			collumn ? 1 : row + 1,
			collumn ? col + 1 : 1
		)
		spreadsheet.selection.setSelectedCell(`${firstCell}:${lastCell}`);
		spreadsheet.selection.setFocusedCell(focused);
	}

	return {
		...clipboardData,
		escape: () => {
			const selected = spreadsheet.selection.getSelectedCell();
			const info = getCellInfo(selected, page, grid);
			const buffer = bufferManager.getStruct();
			buffer.inserted = true;
			if (info.edited) {
				spreadsheet.endEdit(true);
				grid.paint();
			}
		},
		delete: () => {
			if (editedCell) {
				return;
			}
			const selected = spreadsheet.selection.getSelectedCell();
			if (selected) {
				spreadsheet.setValue(selected, "");
			}
			grid.paint();
		},
		enter: () => {
			const selected = spreadsheet.selection.getSelectedCell();
			const focused = spreadsheet.selection.getFocusedCell();
			const editMode = (spreadsheet as any)._editMode;
			const insertedFormula = editLine.navigate("enter");

			if (insertedFormula) {
				return;
			}

			if (editedCell || (editMode && editMode.cell)) {
				const index = getCellIndex(editedCell || editMode.cell, grid);

				index.row = index.row + 1 === grid.data.getLength() ? index.row : index.row + 1;
				spreadsheet.selection.setSelectedCell(getCellNameByIndex(index.row, index.col, grid));
				editedCell = "";
				if (isRangeId(selected) && !editMode) {
					spreadsheet.selection.setSelectedCell(selected);
					spreadsheet.selection.setFocusedCell(getNextRangeCell(selected, focused, grid, "col"));
				}
			} else {
				if (isRangeId(selected)) {
					spreadsheet.endEdit();
					spreadsheet.selection.setSelectedCell(selected);
					spreadsheet.selection.setFocusedCell(getNextRangeCell(selected, focused, grid, "col"));
					return;
				}
				spreadsheet.startEdit(selected);
			}
		},
		f2: () => {
			spreadsheet.startEdit(spreadsheet.selection.getSelectedCell());
		},
		pageUp: e => {
			e.preventDefault();
			const body = grid.getRootView().refs.grid_body;
			body.el.scrollTop -= body.el.clientHeight;
		},
		pageDown: e => {
			e.preventDefault();
			const body = grid.getRootView().refs.grid_body;
			body.el.scrollTop += body.el.clientHeight - grid.config.rowHeight;
		},
		"ctrl+a": e => {
			if (!editedCell) {
				e.preventDefault();
				const first = getCellNameByIndex(0, 1, grid);
				const last = getCellNameByIndex(grid.data.getLength() - 1, grid.config.columns.length - 1, grid);
				spreadsheet.selection.setSelectedCell(`${first}:${last}`);
			}
		},
		"shift+arrowLeft": () => {
			setSelection(spreadsheet, grid, -1, "col", page);
		},
		"shift+arrowRight": () => {
			setSelection(spreadsheet, grid, 1, "col", page);
		},
		"shift+arrowDown": () => {
			setSelection(spreadsheet, grid, 1, "row", page);
		},
		"shift+arrowUp": () => {
			setSelection(spreadsheet, grid, -1, "row", page);
		},
		"ctrl+enter": () => {
			spreadsheet.endEdit();
		},
		"shift+enter": () => {
			const selected = spreadsheet.selection.getSelectedCell();
			if (isRangeId(selected)) {
				const focused = spreadsheet.selection.getFocusedCell();
				spreadsheet.endEdit();
				spreadsheet.selection.setSelectedCell(selected);
				spreadsheet.selection.setFocusedCell(getPrevRangeCell(selected, focused, grid, "col"));
				return;
			}
			setCell(spreadsheet, grid, null, page, -1, "row");
		},
		tab: e => {
			e.preventDefault();
			const selected = spreadsheet.selection.getSelectedCell();
			if (isRangeId(selected)) {
				const focused = spreadsheet.selection.getFocusedCell();
				spreadsheet.endEdit();
				spreadsheet.selection.setSelectedCell(selected);
				spreadsheet.selection.setFocusedCell(getNextRangeCell(selected, focused, grid));

				return;
			}
			setCell(spreadsheet, grid, null, page, 1, "col");
		},
		"shift+tab": e => {
			e.preventDefault();
			const selected = spreadsheet.selection.getSelectedCell();
			if (isRangeId(selected)) {
				const focused = spreadsheet.selection.getFocusedCell();
				spreadsheet.endEdit();
				spreadsheet.selection.setSelectedCell(selected);
				spreadsheet.selection.setFocusedCell(getPrevRangeCell(selected, focused, grid));
				return;
			}
			setCell(spreadsheet, grid, null, page, -1, "col");
		},
		arrowLeft: e => {
			if (editedCell) {
				return;
			}
			e.preventDefault();
			setCell(spreadsheet, grid, null, page, -1, "col");
		},
		arrowRight: e => {
			if (editedCell) {
				return;
			}
			e.preventDefault();
			setCell(spreadsheet, grid, null, page, 1, "col");
		},
		arrowDown: e => {
			e.preventDefault();
			if (editLine.navigate("arrowDown")) {
				return;
			}
			if (editedCell) {
				return;
			}
			setCell(spreadsheet, grid, null, page, 1, "row");
		},
		arrowUp: e => {
			e.preventDefault();
			if (editLine.navigate("arrowUp")) {
				return;
			}

			if (editedCell) {
				return;
			}
			setCell(spreadsheet, grid, null, page, -1, "row");
		},
		"ctrl+arrowLeft": () => {
			setCell(spreadsheet, grid, 1, page, null, "col");
		},
		"ctrl+arrowRight": () => {
			setCell(spreadsheet, grid, grid.config.columns.length - 1, page, null, "col");
		},
		"ctrl+arrowDown": () => {
			setCell(spreadsheet, grid, grid.data.getLength() - 1, page, null, "row");
		},
		"ctrl+arrowUp": () => {
			setCell(spreadsheet, grid, 0, page, null, "row");
		},
		home: e => {
			if (editedCell) {
				return;
			}
			e.preventDefault();
			setCell(spreadsheet, grid, 1, page, null, "col");
		},
		end: e => {
			if (editedCell) {
				return;
			}
			e.preventDefault();
			setCell(spreadsheet, grid, grid.config.columns.length - 1, page, null, "col");
		},
		"ctrl+home": () => {
			setCell(spreadsheet, grid, 0, page, null, "row");
			setCell(spreadsheet, grid, 1, page, null, "col");
		},
		"ctrl+end": () => {
			setCell(spreadsheet, grid, grid.data.getLength() - 1, page, null, "row");
			setCell(spreadsheet, grid, grid.config.columns.length - 1, page, null, "col");
		},
		"ctrl+z": e => {
			const focused = spreadsheet.selection.getFocusedCell();
			const cellInfo = getCellInfo(focused, page, grid);
			if (!focused || !cellInfo.edited) {
				e.preventDefault();
			}
			spreadsheet.undo();
		},
		"ctrl+y": e => {
			e.preventDefault();
			spreadsheet.redo();
		},
		"ctrl+shift+z": () => {
			spreadsheet.redo();
		},
		"ctrl+b": () => {
			const cell = spreadsheet.selection.getSelectedCell();
			const val = getToggledValue(page, cell, "font-weight", "bold", grid);
			spreadsheet.setStyle(spreadsheet.selection.getSelectedCell(), {
				"font-weight": val,
			});
		},
		"ctrl+i": () => {
			const cell = spreadsheet.selection.getSelectedCell();
			const val = getToggledValue(page, cell, "font-style", "italic", grid);
			spreadsheet.setStyle(spreadsheet.selection.getSelectedCell(), {
				"font-style": val,
			});
		},
		"ctrl+u": e => {
			e.preventDefault();
			const cell = spreadsheet.selection.getSelectedCell();
			const val = getToggledValue(page, cell, "text-decoration", "underline", grid);
			spreadsheet.setStyle(spreadsheet.selection.getSelectedCell(), {
				"text-decoration": val,
			});
		},
		"ctrl+f": e => {
			e.preventDefault();
			(spreadsheet as any).search("", true);
		},
		"alt+shift+5": e => {
			if (isMac()) return;
			lineThrough(e);
		},
		"ctrl+shift+x": e => {
			if (!isMac()) return;
			lineThrough(e);
		},
		"ctrl+shift+e": e => {
			textAlign("center", e);
		},
		"ctrl+shift+l": e => {
			textAlign("left", e);
		},
		"ctrl+shift+r": e => {
			textAlign("right", e);
		},
		"shift + F11": e => {
			e.preventDefault();
			spreadsheet.addSheet();
		},
		"alt + arrowUp": e => {
			navigateSheets(1, e);
		},
		"alt + arrowDown": e => {
			navigateSheets(-1, e);
		},
		"ctrl + space": e => {
			selectAction(true, e);
		},
		"shift + space": e => {
			selectAction(false, e);
		},
		"ctrl+k": e => {
			e.preventDefault();
			const cell = spreadsheet.selection.getFocusedCell();
			(spreadsheet as any)._showLinkPopup(cell);
		},
		backspace: () => {
			if (editedCell) {
				return;
			}
			spreadsheet.setValue(spreadsheet.selection.getSelectedCell(), "");
		},
	};
}
